import React from "react";
import { Link } from "react-router-dom";
import { BookCover } from "@/components/BookCover";
import { CornerOrn, EyeMark, GlyphRow, OrnRule } from "@/components/Ornaments";
import { Seo } from "@/components/Seo";
import { CONCEPTS } from "@/content/concepts";
import { VIDEOS, YOUTUBE_PLAYLIST_URL } from "@/content/videos";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

export const Home: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  return (
    <>
      <Seo title={t.meta.home.title} description={t.meta.home.description} lang={lang} path="/" />

      <section className="relative overflow-hidden" style={{ background: "var(--ink)" }}>
        <div className="grain" />
        <div
          className="absolute top-6 left-6 opacity-40 hidden md:block"
          style={{ color: "var(--gold)" }}
        >
          <CornerOrn />
        </div>
        <div
          className="absolute top-6 right-6 opacity-40 hidden md:block"
          style={{ color: "var(--gold)" }}
        >
          <CornerOrn flip />
        </div>

        <div className="max-w-page mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32 grid md:grid-cols-12 gap-10 md:gap-16 items-center relative">
          <div className="md:col-span-7 drift-in" style={{ color: "var(--parchment)" }}>
            <div
              className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[12px] mb-6")}
              style={{ color: "var(--vermillion-2)" }}
            >
              {t.hero.kicker}
            </div>
            <h1
              className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "leading-[1.02]")}
              style={{ fontSize: "clamp(2.6rem, 6vw, 5.2rem)", fontWeight: 600 }}
            >
              {t.hero.title1}
            </h1>
            <h2
              className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif italic", "mt-2 md:mt-4")}
              style={{
                fontSize: "clamp(1.4rem, 3vw, 2.5rem)",
                fontWeight: 400,
                color: "var(--gold-2)",
                letterSpacing: isRTL ? "0" : "-0.01em",
              }}
            >
              {t.hero.title2}
            </h2>

            <div className="my-8 max-w-xl" style={{ color: "var(--muted-dark)" }}>
              <OrnRule color="var(--gold)" />
            </div>

            <p
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-body",
                "text-lg md:text-xl leading-relaxed max-w-2xl",
              )}
              style={{ color: "var(--parchment)", opacity: 0.86 }}
            >
              {t.hero.lede}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                  "text-[13px] md:text-[14px] px-6 py-3.5 rounded-full transition-all hover:brightness-110",
                )}
                style={{
                  background: "var(--vermillion)",
                  color: "#fff3df",
                  boxShadow: "0 10px 30px rgba(200,68,30,.3)",
                }}
              >
                {t.hero.ctaPrimary}
              </Link>
              <Link
                to="/concepts"
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                  "text-[13px] md:text-[14px] px-6 py-3.5 rounded-full",
                )}
                style={{
                  color: "var(--parchment)",
                  border: "1px solid var(--hairline-dark)",
                }}
              >
                {t.hero.ctaSecondary}
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-4" style={{ color: "var(--muted-dark)" }}>
              <GlyphRow color="var(--gold)" scale={0.75} />
              <span className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[11px]")}>
                {t.hero.frameCaption}
              </span>
            </div>
          </div>

          <div className="md:col-span-5 flex justify-center md:justify-end drift-in">
            <div className="relative">
              <div
                className="absolute -bottom-10 inset-x-6 h-16 rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(0,0,0,.55), transparent 70%)",
                  filter: "blur(6px)",
                }}
              />
              <div className="frame-gold">
                <BookCover lang="ar" size="md" />
              </div>
              <div
                className="absolute -top-5 -end-4 px-3 py-2 rounded-full"
                style={{
                  background: "var(--parchment)",
                  color: "var(--ink)",
                  boxShadow: "0 6px 20px rgba(0,0,0,.25)",
                }}
              >
                <span
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[10px]")}
                >
                  {isRTL ? "طبعة ٢٠١٥" : "2015 EDITION"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 md:sticky md:top-28">
              <div
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-3")}
                style={{ color: "var(--vermillion)" }}
              >
                {t.featured.kicker}
              </div>
              <h3
                className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "leading-[1.05]")}
                style={{
                  fontSize: "clamp(2rem, 3.5vw, 3rem)",
                  color: "var(--ink)",
                  fontWeight: 600,
                }}
              >
                {t.featured.title}
              </h3>
            </div>
            <div className="md:col-span-7">
              <div
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-body",
                  "text-lg md:text-xl leading-relaxed",
                )}
                style={{ color: "var(--ink-2)" }}
              >
                <p>{t.featured.body1}</p>
                <p className="mt-5">{t.featured.body2}</p>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-5">
                {t.featured.feature.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 border-t pt-5"
                    style={{ borderColor: "var(--hairline-light)" }}
                  >
                    <div className="cat-num text-[13px]" style={{ color: "var(--vermillion)" }}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "leading-snug")}
                      style={{ color: "var(--ink)" }}
                    >
                      {f}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "text-[13px] px-6 py-3 rounded-full transition-all hover:brightness-110",
                  )}
                  style={{ background: "var(--ink)", color: "var(--parchment)" }}
                >
                  {t.featured.ctaBuy}
                </Link>
                <Link
                  to="/book"
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "text-[13px] px-6 py-3 rounded-full",
                  )}
                  style={{ color: "var(--ink)", border: "1px solid var(--hairline-light)" }}
                >
                  {t.featured.ctaExcerpt}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IDEAS TEASER */}
      <section className="relative" style={{ background: "var(--ink)" }}>
        <div className="grain" />
        <div
          className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-28 relative"
          style={{ color: "var(--parchment)" }}
        >
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-3")}
                style={{ color: "var(--vermillion-2)" }}
              >
                {t.ideas.kicker}
              </div>
              <h3
                className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "leading-[1.05]")}
                style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", fontWeight: 600 }}
              >
                {t.ideas.title}
              </h3>
              <p
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-4 max-w-2xl")}
                style={{ color: "var(--muted-dark)" }}
              >
                {t.ideas.intro}
              </p>
            </div>
            <Link
              to="/concepts"
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                "text-[12px] px-5 py-3 rounded-full",
              )}
              style={{ color: "var(--parchment)", border: "1px solid var(--hairline-dark)" }}
            >
              {t.ideas.seeAll} →
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {CONCEPTS.slice(0, 6).map((c, i) => (
              <article key={i} className="group">
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="cat-num text-[14px]" style={{ color: "var(--vermillion-2)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="flex-1 h-px"
                    style={{ background: "var(--hairline-dark)" }}
                  />
                </div>
                <h4
                  className={cx(
                    isRTL ? "ff-ar-serif" : "ff-en-serif",
                    "text-2xl leading-snug mb-2",
                  )}
                  style={{ color: "var(--parchment)" }}
                >
                  {c[lang].t}
                </h4>
                <p
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "leading-relaxed")}
                  style={{ color: "var(--muted-dark)" }}
                >
                  {c[lang].b}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEOS TEASER */}
      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-3")}
                style={{ color: "var(--vermillion)" }}
              >
                {t.videos.kicker}
              </div>
              <h3
                className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "leading-[1.05]")}
                style={{
                  fontSize: "clamp(2rem, 3.5vw, 3rem)",
                  color: "var(--ink)",
                  fontWeight: 600,
                }}
              >
                {t.videos.title}
              </h3>
              <p
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-4 max-w-2xl")}
                style={{ color: "var(--muted-light)" }}
              >
                {t.videos.intro}
              </p>
            </div>
            <Link
              to="/videos"
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                "text-[12px] px-5 py-3 rounded-full",
              )}
              style={{ color: "var(--ink)", border: "1px solid var(--hairline-light)" }}
            >
              {t.videos.seeAll} →
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VIDEOS.slice(0, 3).map((v, i) => (
              <article
                key={v.id}
                className="group block p-6"
                style={{
                  border: "1px solid var(--hairline-light)",
                  background: "var(--card-surface-warm)",
                }}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="cat-num text-[13px]" style={{ color: "var(--vermillion)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 h-px" style={{ background: "var(--hairline-light)" }} />
                </div>
                <h4
                  className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-xl leading-snug")}
                  style={{ color: "var(--ink)", fontWeight: 600 }}
                >
                  {v[lang].t}
                </h4>
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "mt-2 text-[11px]",
                  )}
                  style={{ color: "var(--muted-light)" }}
                >
                  {v[lang].d}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-5">
            <a
              href={YOUTUBE_PLAYLIST_URL}
              target="_blank"
              rel="noreferrer"
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                "text-[12px] underline",
              )}
              style={{ color: "var(--vermillion)" }}
            >
              {isRTL ? "قناة يوتيوب الرسمية ↗" : "Official YouTube channel ↗"}
            </a>
            <Link
              to="/posts"
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                "text-[12px] underline",
              )}
              style={{ color: "var(--vermillion)" }}
            >
              {isRTL ? "تصفّح منشورات فيسبوك" : "Browse Facebook posts"} →
            </Link>
          </div>
        </div>
      </section>

      {/* LEGACY NOTE */}
      <section className="relative" style={{ background: "var(--ink-2)" }}>
        <div className="grain" />
        <div
          className="max-w-[1000px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center relative"
          style={{ color: "var(--parchment)" }}
        >
          <div className="flex justify-center mb-5" style={{ color: "var(--gold)" }}>
            <EyeMark size={36} />
          </div>
          <div
            className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-4")}
            style={{ color: "var(--vermillion-2)" }}
          >
            {t.legacy.kicker}
          </div>
          <h3
            className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "leading-[1.1] mb-6")}
            style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 600 }}
          >
            {t.legacy.title}
          </h3>
          <p
            className={cx(
              isRTL ? "ff-ar-sans" : "ff-en-body",
              "text-lg md:text-xl leading-relaxed mx-auto max-w-2xl",
            )}
            style={{ color: "var(--muted-dark)" }}
          >
            {t.legacy.body}
          </p>
          <Link
            to="/legacy"
            className={cx(
              "mt-10 inline-block",
              isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
              "text-[12px] px-6 py-3 rounded-full",
            )}
            style={{ color: "var(--parchment)", border: "1px solid var(--hairline-dark)" }}
          >
            {t.legacy.cta} →
          </Link>
        </div>
      </section>
    </>
  );
};
